import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type {
  RailRowMenuController,
  RailRowMenuPoint,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import { ObjectActionMenuCloseProvider } from "@/object-actions/renderers/ObjectActionMenuCloseContext";
import { cn } from "@/utils";
import { MoreHorizontal } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";

/** Marks every rail kebab so an outside-dismiss click on one isn't swallowed. */
const RAIL_ROW_MENU_TRIGGER_ATTR = "data-rail-row-menu-trigger";

// Only one rail row menu is open at a time. Each open menu registers its close
// fn here; opening another closes the rest. The menus are non-modal (see below),
// so without this each row's independent open state could leave several stacked.
const railRowMenuClosers = new Set<() => void>();

/** Default kebab wrapper: absolute right edge, hover-revealed via `group/row`. */
export function railRowKebabWrapperClassName(open: boolean) {
  return cn(
    "absolute right-1.5 top-1/2 z-10 -translate-y-1/2",
    open
      ? "opacity-100"
      : "opacity-0 group-hover/row:opacity-100 focus-within:opacity-100",
  );
}

/**
 * Standardized rail row menu — one shadcn `DropdownMenu` shared by every
 * sub-rail. The content anchors to a **virtual element positioned at `point`**
 * (the cursor on right-click, the kebab's rect on click) so the menu opens where
 * the user invoked it. Pass the row's menu items as `children`.
 */
export function RailRowMenu({
  open,
  onOpenChange,
  point,
  label,
  children,
  kebab = true,
  kebabWrapperClassName,
  onToggleFromButton,
  contentClassName,
  contentWidthClassName = "w-44",
  align = "start",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  point: RailRowMenuPoint;
  /** Resource name — used to label the kebab trigger for assistive tech. */
  label: string;
  children: React.ReactNode;
  /** Render the hover-revealed kebab button (off for cursor-only menus). */
  kebab?: boolean;
  /** Visibility/position wrapper for the kebab (defaults to `group/row` reveal). */
  kebabWrapperClassName?: string;
  /** Kebab click handler — typically the controller's `toggleFromButton`. */
  onToggleFromButton?: (e: React.MouseEvent) => void;
  contentClassName?: string;
  contentWidthClassName?: string;
  align?: "start" | "center" | "end";
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => onOpenChange(false), [onOpenChange]);

  // The menu is non-modal (a modal dropdown + the AlertDialogs some rows open
  // both lock <body> pointer-events and Radix miscounts them, freezing the app).
  // Non-modal means a dismissing click would otherwise fall through and trigger
  // whatever is under the cursor. While open, intercept any outside primary
  // click in the capture phase: close the menu and swallow both the pointerdown
  // and the click it generates so nothing underneath reacts.
  useEffect(() => {
    if (!open) return;

    // Single-open: close any other rail menu, then register this one. Opening a
    // menu on another row (kebab or right-click) thus dismisses the previous.
    const close = () => onOpenChange(false);
    for (const other of [...railRowMenuClosers]) other();
    railRowMenuClosers.add(close);

    const isInsideMenu = (target: EventTarget | null) => {
      if (!(target instanceof Node) || !contentRef.current) return false;
      if (contentRef.current.contains(target)) return true;
      // SubContent is portaled next to Content, not under it (e.g. file export
      // formats). Radix tags both with `data-radix-menu-content`; each submenu
      // points at its SubTrigger via aria-labelledby, and that trigger lives in
      // the parent level's content. Walk the chain upward until we land in our
      // panel — nested cascades (destination submenu) can be several deep.
      if (!(target instanceof Element)) return false;
      let level: Element | null = target.closest("[data-radix-menu-content]");
      for (let depth = 0; level && depth < 8; depth++) {
        if (
          level === contentRef.current ||
          contentRef.current.contains(level)
        ) {
          return true;
        }
        const triggerId = level.getAttribute("aria-labelledby");
        if (!triggerId) return false;
        const trigger = document.getElementById(triggerId);
        if (!trigger) return false;
        if (contentRef.current.contains(trigger)) return true;
        level = trigger.closest("[data-radix-menu-content]");
      }
      return false;
    };

    const onPointerDownCapture = (e: PointerEvent) => {
      // Let non-primary buttons through (right-click re-opens at a new point via
      // the row's onContextMenu; Radix/Escape still handle dismissal).
      if (e.button !== 0 || isInsideMenu(e.target)) return;
      // A primary click on any rail kebab should open/toggle that row's menu —
      // the single-open coordinator closes this one — instead of being swallowed
      // as an outside dismiss, which would make the other kebab need two clicks.
      if (
        e.target instanceof Element &&
        e.target.closest(`[${RAIL_ROW_MENU_TRIGGER_ATTR}]`)
      ) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      onOpenChange(false);
      // Swallow the click this pointerdown will produce, independent of React's
      // re-render timing, so the row underneath never fires its onClick.
      const swallowClick = (ce: MouseEvent) => {
        ce.preventDefault();
        ce.stopPropagation();
      };
      document.addEventListener("click", swallowClick, {
        capture: true,
        once: true,
      });
      window.setTimeout(
        () => document.removeEventListener("click", swallowClick, true),
        300,
      );
    };

    document.addEventListener("pointerdown", onPointerDownCapture, true);
    return () => {
      railRowMenuClosers.delete(close);
      document.removeEventListener("pointerdown", onPointerDownCapture, true);
    };
  }, [open, onOpenChange]);

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
      {/* Virtual anchor — the menu content positions relative to this, so it
          appears at the cursor (right-click) or below the kebab (click). */}
      <DropdownMenuTrigger asChild>
        <span
          aria-hidden
          className="pointer-events-none fixed h-0 w-0"
          style={{ left: point.x, top: point.y }}
        />
      </DropdownMenuTrigger>

      {kebab ? (
        <div
          className={
            kebabWrapperClassName ?? railRowKebabWrapperClassName(open)
          }
        >
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className="size-6 p-0"
            aria-label={`${label} actions`}
            {...{ [RAIL_ROW_MENU_TRIGGER_ATTR]: "" }}
            // Stop pointerdown so (a) the row's dnd-kit drag doesn't arm and
            // (b) Radix's dismiss-on-outside doesn't fire for the kebab, which
            // keeps it a clean toggle even though the trigger is the virtual span.
            onPointerDown={(e) => e.stopPropagation()}
            onClick={onToggleFromButton}
          >
            <MoreHorizontal className="size-4" />
          </Button>
        </div>
      ) : null}

      <DropdownMenuContent
        // Keyed by the anchor point: when a right-click re-anchors an OPEN
        // menu, the dismissing pointerdown and the re-opening contextmenu can
        // land in one React batch — the content never unmounts, and Radix
        // only measures the virtual anchor at mount, so the menu stayed
        // stuck at its first location. A point change forces the remount
        // that re-positions it.
        key={`${point.x}:${point.y}`}
        ref={contentRef}
        align={align}
        side="bottom"
        sideOffset={4}
        // Menu content is portalled out of the rail, so the rail's
        // `cursor: pointer` base rule can't reach its items.
        className={cn(
          "[&_[role=menuitem]]:cursor-pointer",
          contentWidthClassName,
          contentClassName,
        )}
      >
        <ObjectActionMenuCloseProvider value={closeMenu}>
          {children}
        </ObjectActionMenuCloseProvider>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Convenience: build `RailRowMenu` props straight from a controller. */
export function railRowMenuProps(controller: RailRowMenuController) {
  return {
    open: controller.open,
    onOpenChange: controller.setOpen,
    point: controller.point,
    onToggleFromButton: controller.toggleFromButton,
  };
}
